import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWebSocket, EventTypes } from '../context/WebSocketContext';
import { 
  getChargers, 
  getSites, 
  createCharger, 
  deleteCharger,
  restartCharger,
  clearChargerCache,
  setChargerAvailability,
  generateChargerQR,
  forceChargerCommand,
  forceReconnect
} from '../lib/api';
import { StatusBadge } from '../components/common/StatusBadge';
import { ConnectorBadges, ConnectorPowerDisplay } from '../components/common/ConnectorBadges';
import usePageTitle from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Card,
  CardContent,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from '../components/ui/dropdown-menu';
import { 
  Zap, Plus, MoreHorizontal, Trash2, Wifi, WifiOff, 
  Search, RefreshCw, Power, PowerOff, QrCode, RotateCcw,
  ChevronRight, MapPin, Clock, Activity, AlertTriangle,
  Filter, X, Hammer, RefreshCcw, Unlock, Database, FileSpreadsheet
} from 'lucide-react';
import AppChargesExcelDialog from '../components/reports/AppChargesExcelDialog';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { useDebouncedCallback } from 'use-debounce';

// Format "Last seen X min/hours ago"
const formatLastSeen = (lastSeenAt) => {
  if (!lastSeenAt) return '';
  const lastSeen = new Date(lastSeenAt);
  const now = new Date();
  const diffMs = now - lastSeen;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'Parë tani';
  if (diffMins < 60) return `Parë ${diffMins} min më parë`;
  if (diffHours < 24) return `Parë ${diffHours} orë më parë`;
  return `Parë ${diffDays} ditë më parë`;
};

const Chargers = () => {
  usePageTitle('Karikuesit');
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const isModerator = user?.role === 'MODERATOR';
  const canManageChargers = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const { subscribe, chargerStates, connected } = useWebSocket();
  const [searchParams] = useSearchParams();
  const siteIdFromUrl = searchParams.get('site');
  
  const [chargers, setChargers] = useState([]);
  const [filteredChargers, setFilteredChargers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Filters
  const [selectedSite, setSelectedSite] = useState(siteIdFromUrl || 'all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [chargerDialogOpen, setChargerDialogOpen] = useState(false);
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCharger, setSelectedCharger] = useState(null);
  const [excelTarget, setExcelTarget] = useState(null);
  const [availabilityReason, setAvailabilityReason] = useState('');
  
  const [chargerForm, setChargerForm] = useState({
    site_id: '',
    charger_id: '',
    model: '',
    vendor: '',
    serial_number: '',
    ocpp_version: '1.6',
    max_power_kw: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [chargersRes, sitesRes] = await Promise.all([
        getChargers(selectedSite !== 'all' ? selectedSite : undefined),
        getSites()
      ]);
      setChargers(chargersRes.data);
      setSites(sitesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Gabim në ngarkimin e të dhënave');
    } finally {
      setLoading(false);
    }
  }, [selectedSite]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Subscribe to WebSocket events for real-time updates
  useEffect(() => {
    // Helper: update connector in local chargers state
    const updateConnectorInChargers = (chargerId, connectorId, updates) => {
      setChargers(prev => prev.map(charger => {
        if (charger.id !== chargerId && charger.charger_id !== chargerId) return charger;
        const updatedConnectors = (charger.connectors || []).map(conn => {
          if (conn.connector_id === connectorId) {
            return { ...conn, ...updates };
          }
          return conn;
        });
        // Recompute charger-level status from connectors
        const statuses = updatedConnectors.map(c => c.status);
        let chargerStatus = charger.status;
        if (statuses.some(s => s === 'Faulted')) chargerStatus = 'Faulted';
        else if (statuses.some(s => ['Charging', 'Finishing'].includes(s))) chargerStatus = 'Charging';
        else if (statuses.some(s => s === 'Preparing')) chargerStatus = 'Preparing';
        else if (statuses.every(s => s === 'Available')) chargerStatus = 'Available';
        return { ...charger, connectors: updatedConnectors, status: chargerStatus };
      }));
    };

    // Subscribe to connector.status.updated (from OCPP via ws_manager v1)
    const unsubConnectorV1 = subscribe('connector.status.updated', (data) => {
      updateConnectorInChargers(data.charger_id, data.connector_id, {
        status: data.status,
        current_power_kw: data.current_power_kw || 0,
        soc_percent: data.soc_percent ?? null
      });
    });

    // Subscribe to legacy connector status changes (from event_emitter)
    const unsubConnector = subscribe(EventTypes.CONNECTOR_STATUS_CHANGED, (data) => {
      updateConnectorInChargers(data.charger_id, data.connector_id, {
        status: data.new_status,
        current_power_kw: data.current_power_kw || 0
      });
    });

    // Subscribe to connector power updates
    const unsubPower = subscribe(EventTypes.CONNECTOR_POWER_UPDATED, (data) => {
      updateConnectorInChargers(data.charger_id, data.connector_id, {
        current_power_kw: data.current_power_kw,
        status: data.status
      });
    });

    // Subscribe to charger online/offline
    const unsubOnline = subscribe(EventTypes.CHARGER_CONNECTION_ONLINE, (data) => {
      console.log('🔌 Charger Online Event:', data);
      toast.success(`Karikuesi ${data.charger_id} është Online!`, {
        description: 'Karikuesi u lidh me sukses'
      });
      // Update local state immediately
      setChargers(prev => prev.map(charger => {
        if (charger.id === data.charger_id || charger.charger_id === data.charger_id) {
          return { 
            ...charger, 
            is_online: true, 
            status: data.status || 'Available',
            last_seen_at: new Date().toISOString()
          };
        }
        return charger;
      }));
      // Also fetch fresh data to ensure consistency
      setTimeout(() => fetchData(), 500);
    });

    const unsubOffline = subscribe(EventTypes.CHARGER_CONNECTION_OFFLINE, (data) => {
      console.log('🔌 Charger Offline Event:', data);
      toast.warning(`Karikuesi ${data.charger_id} shkoi Offline`, {
        description: 'Lidhja u ndërpre'
      });
      // Update local state immediately
      setChargers(prev => prev.map(charger => {
        if (charger.id === data.charger_id || charger.charger_id === data.charger_id) {
          return { 
            ...charger, 
            is_online: false, 
            status: 'Offline'
          };
        }
        return charger;
      }));
    });

    // Subscribe to charger status changes
    const unsubStatus = subscribe(EventTypes.CHARGER_STATUS_CHANGED, (data) => {
      setChargers(prev => prev.map(charger => {
        if (charger.id === data.charger_id || charger.charger_id === data.charger_id) {
          return { ...charger, status: data.new_status, is_online: data.is_online };
        }
        return charger;
      }));
    });

    // Subscribe to charger health changes (online/offline based on last_seen_at)
    const unsubHealth = subscribe('*', (event) => {
      if (event.type === 'charger.health_changed') {
        const data = event.data || {};
        console.log('🔌 Charger Health Changed:', data);
        
        // Show notification based on status change
        if (data.is_online) {
          toast.success(`Karikuesi ${data.charger_id} u kthye Online`);
        } else {
          toast.warning(`Karikuesi ${data.charger_id} u bë Offline`);
        }
        
        setChargers(prev => prev.map(charger => {
          if (charger.charger_id === data.charger_id || charger.id === data.charger_id) {
            return { 
              ...charger, 
              is_online: data.is_online,
              last_seen_at: data.last_seen_at,
              status: data.is_online ? (charger.status === 'Offline' ? 'Available' : charger.status) : 'Offline'
            };
          }
          return charger;
        }));
      }
    });

    // Subscribe to session progress for live power/SoC updates
    const unsubProgress = subscribe('*', (event) => {
      if (event.type === 'session.progress' || event.type === 'session.updated') {
        const data = event.data || {};
        if (data.charger_id) {
          setChargers(prev => prev.map(charger => {
            // Match by charger_id (could be ID or charger_id string)
            if (charger.id === data.charger_id || charger.charger_id === data.charger_id) {
              const updatedConnectors = (charger.connectors || []).map(conn => {
                if (conn.connector_id === data.connector_id) {
                  return {
                    ...conn,
                    current_power_kw: data.power_kw ?? data.current_power_kw ?? conn.current_power_kw,
                    soc_percent: data.soc_percent ?? conn.soc_percent,
                    energy_kwh: data.energy_kwh ?? conn.energy_kwh
                  };
                }
                return conn;
              });
              return { ...charger, connectors: updatedConnectors };
            }
            return charger;
          }));
        }
      }
    });

    return () => {
      unsubConnectorV1();
      unsubConnector();
      unsubPower();
      unsubOnline();
      unsubOffline();
      unsubStatus();
      unsubHealth();
      unsubProgress();
    };
  }, [subscribe]);

  // Apply filters
  useEffect(() => {
    let result = [...chargers];
    
    if (statusFilter !== 'all') {
      // Handle online/offline as special status filters
      if (statusFilter === 'online') {
        result = result.filter(c => c.is_online === true);
      } else if (statusFilter === 'offline') {
        result = result.filter(c => c.is_online === false);
      } else {
        result = result.filter(c => c.status === statusFilter);
      }
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c => 
        c.charger_id?.toLowerCase().includes(query) ||
        c.model?.toLowerCase().includes(query) ||
        c.vendor?.toLowerCase().includes(query) ||
        c.serial_number?.toLowerCase().includes(query)
      );
    }
    
    setFilteredChargers(result);
  }, [chargers, statusFilter, searchQuery]);

  const debouncedSearch = useDebouncedCallback((value) => {
    setSearchQuery(value);
  }, 300);

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site?.name || 'Pa vendndodhje';
  };

  const getSiteCity = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site?.city || '';
  };

  // Actions
  const handleRestart = async (charger) => {
    setActionLoading(charger.id);
    try {
      await restartCharger(charger.id);
      toast.success('Komanda e rinisjes u dërgua', {
        description: `Karikuesi ${charger.charger_id} po riniset...`
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në rinisjen e karikuesit');
    } finally {
      setActionLoading(null);
    }
  };

  const handleClearCache = async (charger) => {
    setActionLoading(charger.id);
    try {
      await clearChargerCache(charger.id);
      toast.success('Cache u pastrua', {
        description: `Cache e karikuesit ${charger.charger_id} u pastrua.`
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në pastrimin e cache');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSetAvailability = async (available) => {
    if (!selectedCharger) return;
    setActionLoading(selectedCharger.id);
    try {
      await setChargerAvailability(selectedCharger.id, available, availabilityReason);
      toast.success(available ? 'Karikuesi u aktivizua' : 'Karikuesi u çaktivizua', {
        description: `Statusi i ${selectedCharger.charger_id} u ndryshua.`
      });
      fetchData();
      setAvailabilityDialogOpen(false);
      setAvailabilityReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në ndryshimin e statusit');
    } finally {
      setActionLoading(null);
    }
  };

  const handleGenerateQR = async (charger) => {
    setActionLoading(charger.id);
    try {
      const result = await generateChargerQR(charger.id);
      toast.success('QR kodet u gjeneruan', {
        description: `${result.data.qr_codes?.length || 0} QR kode u krijuan.`
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në gjenerimin e QR');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedCharger) return;
    setActionLoading(selectedCharger.id);
    try {
      await deleteCharger(selectedCharger.id);
      toast.success('Karikuesi u fshi');
      fetchData();
      setDeleteDialogOpen(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Gabim në fshirjen e karikuesit';
      toast.error(message);
    } finally {
      setActionLoading(null);
      setSelectedCharger(null);
    }
  };

  const handleCreateCharger = async (e) => {
    e.preventDefault();
    try {
      await createCharger({
        ...chargerForm,
        max_power_kw: parseFloat(chargerForm.max_power_kw)
      });
      toast.success('Karikuesi u krijua', {
        description: `ID: ${chargerForm.charger_id}`
      });
      setChargerDialogOpen(false);
      setChargerForm({
        site_id: '',
        charger_id: '',
        model: '',
        vendor: '',
        serial_number: '',
        ocpp_version: '1.6',
        max_power_kw: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në krijimin e karikuesit');
    }
  };

  // Force Commands (Admin only)
  const handleForceReset = async (charger, resetType = 'Soft') => {
    setActionLoading(charger.id);
    try {
      const response = await forceChargerCommand(charger.charger_id, 'reset', resetType);
      if (response.data.success) {
        toast.success(`Reset ${resetType} u dërgua`, {
          description: response.data.message
        });
      } else {
        toast.warning('Komanda u dërgua', {
          description: response.data.message
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në dërgimin e komandës');
    } finally {
      setActionLoading(null);
    }
  };

  const handleForceUnlock = async (charger) => {
    setActionLoading(charger.id);
    try {
      const response = await forceChargerCommand(charger.charger_id, 'unlock', 'Soft', 1);
      toast.success('Unlock u dërgua', {
        description: response.data.message
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në dërgimin e komandës');
    } finally {
      setActionLoading(null);
    }
  };

  const handleForceClearCache = async (charger) => {
    setActionLoading(charger.id);
    try {
      const response = await forceChargerCommand(charger.charger_id, 'clear_cache');
      toast.success('Clear Cache u dërgua', {
        description: response.data.message
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në dërgimin e komandës');
    } finally {
      setActionLoading(null);
    }
  };

  const handleForceReconnect = async (charger) => {
    setActionLoading(charger.id);
    try {
      const response = await forceReconnect(charger.charger_id);
      if (response.data.is_online) {
        toast.success('Karikuesi është Online', {
          description: response.data.message
        });
      } else {
        toast.info('Karikuesi është Offline', {
          description: response.data.message
        });
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në sinkronizim');
    } finally {
      setActionLoading(null);
    }
  };

  const clearFilters = () => {
    setSelectedSite('all');
    setStatusFilter('all');
    setSearchQuery('');
  };

  const hasActiveFilters = selectedSite !== 'all' || statusFilter !== 'all' || searchQuery;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-10 w-48" />
        <div className="skeleton h-96 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="chargers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground font-outfit">Karikuesit</h1>
            <p className="text-muted-foreground mt-1">
              {filteredChargers.length} nga {chargers.length} karikues
            </p>
          </div>
          {/* Live indicator */}
          {connected && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              <span className="text-xs font-medium text-green-500">Live</span>
            </div>
          )}
        </div>
        {canManageChargers && (
          <Button onClick={() => setChargerDialogOpen(true)} data-testid="add-charger-btn">
            <Plus className="w-4 h-4 mr-2" />
            Shto Karikues
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <Input
                placeholder="Kërko sipas ID, model, vendor..."
                onChange={(e) => debouncedSearch(e.target.value)}
                className="flex-1 text-sm"
                data-testid="search-input"
              />
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger className="w-full sm:w-40 text-xs sm:text-sm" data-testid="site-filter">
                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                  <SelectValue placeholder="Vendndodhja" />
                </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Të gjitha vendet</SelectItem>
                {sites.map((site) => (
                  <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-32 text-xs sm:text-sm" data-testid="status-filter">
                <Activity className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                <SelectValue placeholder="Statusi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Të gjithë</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
                <SelectItem value="Available">I Lirë</SelectItem>
                <SelectItem value="Charging">Duke karikuar</SelectItem>
                <SelectItem value="Preparing">Përgatitje</SelectItem>
                <SelectItem value="Reconnecting">Duke u rilidhur</SelectItem>
                <SelectItem value="Faulted">Me defekt</SelectItem>
                <SelectItem value="Unavailable">I padisponueshëm</SelectItem>
              </SelectContent>
            </Select>
            </div>
            
            <div className="flex items-center gap-2 w-full sm:w-auto">
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">
                <X className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                <span className="hidden sm:inline">Pastro filtrat</span>
                <span className="sm:hidden">Pastro</span>
              </Button>
            )}
            
            <Button variant="outline" size="icon" onClick={fetchData} data-testid="refresh-btn" className="w-8 h-8 sm:w-9 sm:h-9">
              <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4" />
            </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Chargers Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0 overflow-x-auto">
          {filteredChargers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Zap className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-sm">
                {chargers.length === 0 ? 'Nuk ka karikues të regjistruar' : 'Nuk ka rezultate me këto filtra'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground text-xs whitespace-nowrap">Karikuesi</TableHead>
                  <TableHead className="text-muted-foreground text-xs whitespace-nowrap hidden md:table-cell">Vendndodhja</TableHead>
                  <TableHead className="text-muted-foreground text-xs whitespace-nowrap">Statusi</TableHead>
                  <TableHead className="text-muted-foreground text-xs whitespace-nowrap hidden lg:table-cell">OCPP</TableHead>
                  <TableHead className="text-muted-foreground text-xs whitespace-nowrap hidden xl:table-cell">Konektorë</TableHead>
                  <TableHead className="text-muted-foreground text-xs whitespace-nowrap hidden lg:table-cell">Fuqia</TableHead>
                  <TableHead className="text-muted-foreground text-xs whitespace-nowrap hidden xl:table-cell">Heartbeat</TableHead>
                  <TableHead className="text-muted-foreground w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredChargers.map((charger) => (
                  <TableRow 
                    key={charger.id} 
                    className="border-border hover:bg-muted/50 cursor-pointer group"
                    onClick={() => navigate(`/chargers/${charger.id}`)}
                    data-testid={`charger-row-${charger.charger_id}`}
                  >
                    <TableCell className="py-2 sm:py-3">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          charger.status === 'Faulted' ? 'bg-red-500/10' :
                          charger.is_online ? 'bg-green-500/10' : 'bg-muted'
                        }`}>
                          {charger.status === 'Faulted' ? (
                            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                          ) : charger.is_online ? (
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" viewBox="0 0 512 512" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                              <path d="m497 165.12c0-82.78-67.34-150.12-150.12-150.12-63.36 0-117.67 39.46-139.67 95.09h-132.08c-20.37 0-36.94 16.57-36.94 36.94v292.59h-17.19c-3.31 0-6 2.69-6 6v45.38c0 3.31 2.69 6 6 6h268.6c3.31 0 6-2.69 6-6v-45.38c0-3.31-2.69-6-6-6h-17.19v-20.92h15.67c3.31 0 6-2.69 6-6v-11.85h12.57c31.01 0 56.24-25.23 56.24-56.24v-30.24c75.27-8.01 134.11-71.88 134.11-149.25zm-213.4 319.88h-256.6v-33.38h256.6zm-233.41-45.38v-292.59c0-13.75 11.19-24.94 24.94-24.94h127.93c-4.09 13.64-6.29 28.08-6.29 43.03 0 50.6 25.17 95.43 63.64 122.64v151.86zm231.89-32.92h-9.67v-43.69h9.67zm-9.67-55.69v-55.59c17.58 10.09 37.38 16.71 58.48 18.96v30.24c0 13.36-10.87 24.24-24.24 24.24h-12.57v-11.85c0-3.31-2.69-6-6-6zm78.47-6.39c0 24.39-19.84 44.24-44.24 44.24h-12.57v-8h12.57c19.98 0 36.24-16.26 36.24-36.24v-29.44c1.33.03 2.66.05 4 .05s2.67-.02 4-.05zm-4-41.39c-44.42 0-84.01-21.09-109.29-53.77l15.67-15.67 3.97 3.97c1.17 1.17 2.71 1.76 4.24 1.76s3.07-.59 4.24-1.76l11.29-11.29 4.21 4.21c1.17 1.17 2.71 1.76 4.24 1.76s3.07-.59 4.24-1.76l18.21-18.21c.75-.74 1.28-1.67 1.55-2.69l5.46-20.38 24.61-24.61h21.75l12.48 12.48c1.12 1.12 2.65 1.76 4.24 1.76h40.96c3.31 0 6-2.69 6-6v-6.93h16.69c3.31 0 6-2.69 6-6v-38.97c0-3.31-2.69-6-6-6h-16.69v-6.93c0-1.59-.63-3.12-1.76-4.24-1.12-1.12-2.65-1.76-4.24-1.76h-81.92c-1.59 0-3.12.63-4.24 1.76l-84.92 84.92c-1.13 1.13-1.76 2.65-1.76 4.24s.63 3.12 1.76 4.24l4.21 4.21-11.29 11.29c-2.34 2.34-2.34 6.14 0 8.49l3.97 3.97-14.21 14.21c-13.79-21.49-21.8-47.03-21.8-74.4.02-76.17 61.98-138.13 138.13-138.13s138.12 61.96 138.12 138.12c0 76.15-61.96 138.11-138.12 138.11zm-86.26-110.12 78.92-78.92h73.44v52.83h-32.48l-12.48-12.48c-1.12-1.12-2.65-1.76-4.24-1.76h-26.72c-1.59 0-3.12.63-4.24 1.76l-27.51 27.51c-.75.74-1.28 1.67-1.55 2.69l-5.46 20.38-12.83 12.83zm-.04 16.94 7.94 7.94-7.04 7.04-7.94-7.94zm164.4-82.93h10.69v26.97h-10.69zm-230.54 130.7h-25.5l20.75-56.95c.67-1.84.4-3.89-.72-5.49s-2.96-2.56-4.92-2.56h-34.07c-2.52 0-4.77 1.58-5.64 3.95l-33.81 92.8c-.67 1.84-.4 3.89.72 5.49s2.96 2.56 4.92 2.56h26.76v65.28c0 2.82 1.96 5.26 4.72 5.86.43.09.86.14 1.28.14 2.3 0 4.46-1.33 5.45-3.5l45.51-99.08c.85-1.86.7-4.02-.4-5.74s-3.01-2.76-5.05-2.76zm-39.51 77.64v-43.85c0-3.31-2.69-6-6-6h-24.19l29.44-80.8h21.3l-20.75 56.95c-.67 1.84-.4 3.89.72 5.49s2.96 2.56 4.92 2.56h24.71z" />
                            </svg>
                          ) : (
                            <WifiOff className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-foreground text-xs sm:text-sm truncate max-w-[100px] sm:max-w-none">{charger.charger_id}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[100px] sm:max-w-none">{charger.vendor} - {charger.model}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="min-w-0">
                        <p className="text-foreground text-sm truncate max-w-[120px]">{getSiteName(charger.site_id)}</p>
                        <p className="text-xs text-muted-foreground truncate max-w-[120px]">{getSiteCity(charger.site_id)}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {/* Combined Status - shows status with online/offline indicator */}
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          {charger.is_online ? (
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                            </span>
                          ) : (
                            <span className="w-2 h-2 rounded-full bg-zinc-500" />
                          )}
                          <StatusBadge 
                            status={charger.status} 
                            isOnline={charger.is_online} 
                            showDot={false}
                            socPercent={charger.status === 'Charging' ? 
                              (charger.connectors || []).reduce((soc, c) => c.soc_percent ?? soc, null) : null
                            }
                          />
                        </div>
                        {!charger.is_online && charger.last_seen_at && (
                          <span className="text-[10px] text-zinc-500 ml-4">
                            {formatLastSeen(charger.last_seen_at)}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-sm text-muted-foreground font-mono">
                        {charger.ocpp_version}
                      </span>
                    </TableCell>
                    <TableCell>
                      <ConnectorBadges 
                        connectors={charger.connectors || []}
                        isChargerOnline={charger.is_online}
                        maxPowerKw={charger.max_power_kw}
                        chargerStatus={charger.status}
                      />
                    </TableCell>
                    <TableCell>
                      <ConnectorPowerDisplay 
                        connectors={charger.connectors || []}
                        maxPowerKw={charger.max_power_kw}
                        isChargerOnline={charger.is_online}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-muted-foreground text-xs">
                        <Clock className="w-3 h-3" />
                        {charger.last_heartbeat ? formatDate(charger.last_heartbeat) : '-'}
                      </div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                            disabled={actionLoading === charger.id}
                            data-testid={`actions-${charger.charger_id}`}
                          >
                            {actionLoading === charger.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <MoreHorizontal className="w-4 h-4" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/chargers/${charger.id}`)}>
                            <ChevronRight className="w-4 h-4 mr-2" />
                            Shiko detajet
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setExcelTarget({
                              id: charger.id,
                              name: charger.name || charger.charger_id,
                            })}
                          >
                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                            Eksporto Excel
                          </DropdownMenuItem>
                          {canManageChargers && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleRestart(charger)}
                                disabled={!charger.is_online}
                              >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Rindiz
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleClearCache(charger)}>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Pastro Cache
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleGenerateQR(charger)}>
                                <QrCode className="w-4 h-4 mr-2" />
                                Gjenero QR
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedCharger(charger);
                                  setAvailabilityDialogOpen(true);
                                }}
                              >
                                {charger.status === 'Unavailable' ? (
                                  <>
                                    <Power className="w-4 h-4 mr-2" />
                                    Aktivizo
                                  </>
                                ) : (
                                  <>
                                    <PowerOff className="w-4 h-4 mr-2" />
                                    Çaktivizo
                                  </>
                                )}
                              </DropdownMenuItem>
                            </>
                          )}
                          
                          {/* Force Commands - Admin Only */}
                          {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger className="text-yellow-500">
                                  <Hammer className="w-4 h-4 mr-2" />
                                  Komanda Force
                                </DropdownMenuSubTrigger>
                                <DropdownMenuPortal>
                                  <DropdownMenuSubContent>
                                    <DropdownMenuItem onClick={() => handleForceReset(charger, 'Soft')}>
                                      <RefreshCcw className="w-4 h-4 mr-2" />
                                      Soft Reset
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleForceReset(charger, 'Hard')}>
                                      <RotateCcw className="w-4 h-4 mr-2 text-red-400" />
                                      Hard Reset
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleForceUnlock(charger)}>
                                      <Unlock className="w-4 h-4 mr-2" />
                                      Unlock Porta A
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleForceClearCache(charger)}>
                                      <Database className="w-4 h-4 mr-2" />
                                      Clear Cache
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleForceReconnect(charger)}>
                                      <RefreshCw className="w-4 h-4 mr-2 text-blue-400" />
                                      Sinkronizo Statusin
                                    </DropdownMenuItem>
                                  </DropdownMenuSubContent>
                                </DropdownMenuPortal>
                              </DropdownMenuSub>
                            </>
                          )}
                          
                          {user?.role === 'ADMIN' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedCharger(charger);
                                  setDeleteDialogOpen(true);
                                }}
                                className="text-red-500 focus:text-red-500"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Fshi
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Charger Dialog */}
      <Dialog open={chargerDialogOpen} onOpenChange={setChargerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit">Shto Karikues të Ri</DialogTitle>
            <DialogDescription>Plotësoni të dhënat e karikuesit për ta regjistruar në sistem</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCharger} className="space-y-4">
            <div className="space-y-2">
              <Label>Vendndodhja</Label>
              <Select 
                value={chargerForm.site_id} 
                onValueChange={(v) => setChargerForm({ ...chargerForm, site_id: v })}
              >
                <SelectTrigger data-testid="charger-site-select">
                  <SelectValue placeholder="Zgjidh vendndodhjen" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="charger_id">Charger ID</Label>
                <Input
                  id="charger_id"
                  value={chargerForm.charger_id}
                  onChange={(e) => setChargerForm({ ...chargerForm, charger_id: e.target.value })}
                  placeholder="CP001"
                  required
                  data-testid="charger-id-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="serial_number">Serial Number</Label>
                <Input
                  id="serial_number"
                  value={chargerForm.serial_number}
                  onChange={(e) => setChargerForm({ ...chargerForm, serial_number: e.target.value })}
                  placeholder="SN123456"
                  required
                  data-testid="charger-serial-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vendor">Prodhuesi</Label>
                <Input
                  id="vendor"
                  value={chargerForm.vendor}
                  onChange={(e) => setChargerForm({ ...chargerForm, vendor: e.target.value })}
                  placeholder="ABB"
                  required
                  data-testid="charger-vendor-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model">Modeli</Label>
                <Input
                  id="model"
                  value={chargerForm.model}
                  onChange={(e) => setChargerForm({ ...chargerForm, model: e.target.value })}
                  placeholder="Terra AC W22"
                  required
                  data-testid="charger-model-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Protokolli OCPP</Label>
                <Select 
                  value={chargerForm.ocpp_version} 
                  onValueChange={(v) => setChargerForm({ ...chargerForm, ocpp_version: v })}
                >
                  <SelectTrigger data-testid="charger-ocpp-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1.6">OCPP 1.6J</SelectItem>
                    <SelectItem value="2.0.1">OCPP 2.0.1</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_power_kw">Fuqia Max (kW)</Label>
                <Input
                  id="max_power_kw"
                  type="number"
                  step="0.1"
                  value={chargerForm.max_power_kw}
                  onChange={(e) => setChargerForm({ ...chargerForm, max_power_kw: e.target.value })}
                  placeholder="22"
                  required
                  data-testid="charger-power-input"
                />
              </div>
            </div>
            
            <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
              <h4 className="text-sm font-medium text-primary mb-2 flex items-center gap-2">
                <Wifi className="w-4 h-4" />
                URL për lidhjen OCPP
              </h4>
              <code className="text-xs text-green-500 bg-muted px-2 py-1 rounded block break-all font-mono">
                wss://ocpp.albmove.al/{chargerForm.ocpp_version || '1.6'}/{chargerForm.charger_id || 'CHARGER_ID'}
              </code>
              <p className="text-xs text-muted-foreground mt-2">
                💡 Konfiguro këtë URL në karikuesin tuaj fizik, pastaj rinise dhe kontrollo te "OCPP Logs"
              </p>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setChargerDialogOpen(false)}>
                Anulo
              </Button>
              <Button type="submit" data-testid="save-charger-btn">Krijo Karikuesin</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Availability Dialog */}
      <Dialog open={availabilityDialogOpen} onOpenChange={setAvailabilityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit">
              {selectedCharger?.status === 'Unavailable' ? 'Aktivizo Karikuesin' : 'Çaktivizo Karikuesin'}
            </DialogTitle>
            <DialogDescription>
              {selectedCharger?.status === 'Unavailable' 
                ? 'Karikuesi do të bëhet i disponueshëm për përdorim.'
                : 'Karikuesi do të shënohet si i padisponueshëm dhe nuk do të pranojë seanca të reja.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Arsyeja (opsionale)</Label>
              <Input
                value={availabilityReason}
                onChange={(e) => setAvailabilityReason(e.target.value)}
                placeholder="P.sh. Mirëmbajtje e planifikuar"
                data-testid="availability-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAvailabilityDialogOpen(false)}>
              Anulo
            </Button>
            <Button 
              onClick={() => handleSetAvailability(selectedCharger?.status === 'Unavailable')}
              disabled={actionLoading}
              data-testid="confirm-availability-btn"
            >
              {selectedCharger?.status === 'Unavailable' ? 'Aktivizo' : 'Çaktivizo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit text-red-500">Fshi Karikuesin</DialogTitle>
            <DialogDescription>
              Jeni të sigurt që dëshironi ta fshini karikuesin {selectedCharger?.charger_id}? 
              Ky veprim nuk mund të zhbëhet.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Anulo
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDelete}
              disabled={actionLoading}
              data-testid="confirm-delete-btn"
            >
              Fshi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AppChargesExcelDialog
        open={!!excelTarget}
        onOpenChange={(open) => { if (!open) setExcelTarget(null); }}
        scope="charger"
        targetId={excelTarget?.id}
        targetName={excelTarget?.name}
      />
    </div>
  );
};

export default Chargers;
